import request from '#api/axios';
import { queryKeyFactory } from '#api/queryKeyFactory';

import {
  CreateMember,
  CreateMemberInfoDto,
  CreatePtMembershipByCoachDto,
  FindMemberInfoResponseDto,
  FindTotalLectureCountForMemberDto,
  MemberLatestLecture,
  MembersLectureData,
  MembershipInfo,
  PatchMember,
  PtMembershipDto
} from '#types/member';
import {
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';
import { AxiosError } from 'axios';

export const createMember = async (payload: CreateMember) => {
  const res = await request({
    method: 'POST',
    url: '/members',
    data: payload
  });
  return res.data;
};

export const getMemberLectureData = async (
  personId: string,
  lectureId: string
) => {
  if (!lectureId) return;

  const res = await request<MembersLectureData>({
    method: 'GET',
    url: `/members/${personId}/latest-lecture`,
    params: {
      lectureId
    }
  });

  return res.data;
};

export const useGetMemberLectureData = ({
  personId,
  lectureId,
  disable
}: {
  personId: string;
  lectureId: string;
  disable?: boolean;
}) => {
  return useQuery(
    queryKeyFactory.GET_MEMBER_LECTURE_DATA(personId, lectureId),
    () => getMemberLectureData(personId, lectureId),
    {
      enabled: !disable && !!personId && !!lectureId
    }
  );
};

export const getMember = async (personId: string) => {
  const res = await request<FindMemberInfoResponseDto>({
    method: 'GET',
    url: `/members/${personId}`
  });
  return res.data;
};

export const useGetMember = (
  personId: string,
  options?: UseQueryOptions<FindMemberInfoResponseDto, AxiosError>
) => {
  return useQuery<FindMemberInfoResponseDto, AxiosError>(
    queryKeyFactory.GET_MEMBER(personId),
    () => getMember(personId),
    {
      ...options,
      enabled: !!personId
    }
  );
};

const patchMember = async (personId: string, data: PatchMember) => {
  const res = await request({
    method: 'PATCH',
    url: `/members/${personId}`,
    data
  });
  return res.data;
};

export const usePatchMember = (
  options: UseMutationOptions<any, unknown, any>
) => {
  return useMutation<any, unknown, any>({
    mutationFn: ({ personId, data }) => patchMember(personId, data),
    ...options
  });
};

export const postMemberInfoByCoach = async (
  personId: string,
  data: CreateMemberInfoDto
) => {
  const res = await request({
    method: 'POST',
    url: `/members/info/${personId}/by-coach`,
    data
  });
  return res.data;
};

// usePostMemberInfoByCoach 훅 정의
type PostMemberInfoByCoachOptions = UseMutationOptions<
  any, // 반환 타입을 명확히 지정할 수 있다면, 그 타입으로 변경하세요
  unknown, // 에러 타입
  { personId: string; data: CreateMemberInfoDto } // 변수 타입
>;

export const usePostMemberInfoByCoach = (
  options?: PostMemberInfoByCoachOptions
) => {
  return useMutation<
    any,
    unknown,
    { personId: string; data: CreateMemberInfoDto }
  >({
    mutationFn: ({ personId, data }) => postMemberInfoByCoach(personId, data),
    ...options
  });
};

const getMemberMemo = async (personId: string) => {
  const res = await request<string>({
    method: 'GET',
    url: `/members/${personId}/memo`
  });
  return res.data;
};

export const useGetMemberMemo = (
  personId: string,
  options?: UseQueryOptions<string, AxiosError>
) => {
  return useQuery<string, AxiosError>(
    queryKeyFactory.GET_MEMBER_MEMO(personId),
    () => getMemberMemo(personId),
    {
      enabled: !!personId,
      ...options
    }
  );
};

const patchMemberMemo = async (personId: string, memo: string) => {
  const res = await request({
    method: 'PATCH',
    url: `/members/${personId}/memo`,
    data: {
      memo
    }
  });
  return res.data;
};

export const usePatchMemberMemo = (
  options: UseMutationOptions<any, unknown, { personId: string; memo: string }>
) => {
  return useMutation<any, unknown, { personId: string; memo: string }>({
    mutationFn: ({ personId, memo }) => patchMemberMemo(personId, memo),
    ...options
  });
};

const getMemberships = async ({
  personId,
  page = 0,
  offset = 10
}: {
  personId: string;
  page?: number;
  offset?: number;
}) => {
  const res = await request<string[]>({
    method: 'GET',
    url: `/members/${personId}/training-pass?page=${page}&offset=${offset}`
  });
  return res.data;
};

export const useGetMemberships = (
  personId: string,
  options?: UseQueryOptions<string[], AxiosError>
) => {
  return useQuery<string[], AxiosError>(
    queryKeyFactory.GET_MEMBERSHIPS(personId),
    () => getMemberships({ personId }),
    {
      enabled: !!personId,
      ...options
    }
  );
};

const postMemberships = async (
  personId: string,
  params: CreatePtMembershipByCoachDto
) => {
  const res = await request({
    method: 'POST',
    url: `/members/${personId}/training-pass`,
    data: params
  });

  return res.data;
};

export const usePostMemberships = (
  options: UseMutationOptions<
    any,
    unknown,
    { personId: string; params: CreatePtMembershipByCoachDto }
  >
) => {
  return useMutation<
    any,
    unknown,
    { personId: string; params: CreatePtMembershipByCoachDto }
  >({
    mutationFn: ({ personId, params }) => postMemberships(personId, params),
    ...options
  });
};

const getLatestMembership = async (personId: string) => {
  const res = await request<PtMembershipDto | null>({
    method: 'GET',
    url: `/members/${personId}/training-pass/latest`
  });
  return res.data;
};

export const useGetLatestMembership = (
  personId: string,
  options?: UseQueryOptions<PtMembershipDto | null, AxiosError>
) => {
  return useQuery<PtMembershipDto | null, AxiosError>(
    queryKeyFactory.GET_LATEST_MEMBERSHIP(personId),
    () => getLatestMembership(personId),
    {
      enabled: !!personId,
      ...options
    }
  );
};

interface GetMembership {
  personId: string;
  confirmationFormId: string;
}

const getMembership = async ({
  personId,
  confirmationFormId
}: GetMembership) => {
  const res = await request<PtMembershipDto>({
    method: 'GET',
    url: `/members/${personId}/training-pass/${confirmationFormId}`
  });
  return res.data;
};

export const useGetMembership = (
  params: GetMembership,
  options?: UseQueryOptions<PtMembershipDto, AxiosError>
) => {
  return useQuery<PtMembershipDto, AxiosError>(
    queryKeyFactory.GET_MEMBERSHIP(params.personId, params.confirmationFormId),
    () => getMembership(params),
    {
      enabled: !!params.confirmationFormId && !!params.personId,
      ...options
    }
  );
};

const getLatestLecture = async ({
  personId,
  lectureId
}: {
  personId: string;
  lectureId: string;
}) => {
  const res = await request<MemberLatestLecture>({
    method: 'GET',
    url: `/members/${personId}/latest-lecture`,
    params: {
      lectureId
    }
  });
  return res.data;
};

export const useGetLatestLecture = (
  personId: string,
  lectureId: string,
  options?: UseQueryOptions<MemberLatestLecture, AxiosError>
) => {
  return useQuery<MemberLatestLecture, AxiosError>(
    queryKeyFactory.GET_LATEST_LECTURE(personId),
    () => getLatestLecture({ personId, lectureId }),
    {
      enabled: !!personId,
      ...options
    }
  );
};

const postMembership = async (
  personId: string,
  params: CreatePtMembershipByCoachDto
) => {
  const res = await request({
    method: 'POST',
    url: `/members/${personId}/training-pass`,
    data: {
      ...params,
      round: params.round === 0 ? null : params.round
    }
  });
  return res.data;
};

export const usePostMembership = (
  options: UseMutationOptions<any, unknown, { personId: string; params: any }>
) => {
  const queryClient = useQueryClient();

  return useMutation<any, unknown, { personId: string; params: any }>({
    mutationFn: ({ personId, params }) => postMembership(personId, params),
    onSuccess: personId => {
      queryClient.invalidateQueries({
        queryKey: queryKeyFactory.GET_MEMBERSHIPS(personId)
      });
    },
    ...options
  });
};

interface IResponse<T> {
  page: number;
  offset: number;
  total: number;
  data: T;
}

const getMembershipList = async (personId: string) => {
  const res = await request<IResponse<MembershipInfo>>({
    method: 'get',
    url: `/members/${personId}/training-pass?offset=1000`
  });

  return res.data;
};

export const useGetMembershipList = (personId: string) => {
  return useQuery(
    queryKeyFactory.GET_MEMBERSHIP_LIST(personId),
    () => getMembershipList(personId),
    {
      enabled: !!personId
    }
  );
};

// 수강권 검색
export const searchMembership = async (personId: string, query: string) => {
  const res = await request({
    method: 'GET',
    url: `/members/${personId}/training-pass/search`,
    params: {
      query
    }
  });

  return res.data;
};

// 결제 요청 알림톡 재발송 (하루에 한 번만 가능)
export const requestPaymentTalk = async (
  personId: string,
  confirmationFormId: string
) => {
  const res = await request({
    method: 'POST',
    url: `/members/${personId}/alim-talk/request-payment/${confirmationFormId}`,
    params: {
      personId,
      confirmationFormId
    }
  });

  return res.data;
};

const patchMemberAllowSelfScheduling = async (
  personId: string,
  allowSelfScheduling: boolean
) => {
  const res = await request({
    method: 'PATCH',
    url: `/members/${personId}/allow-scheduling`,
    data: {
      allowSelfScheduling
    }
  });
  return res.data;
};

export const usePatchMemberAllowSelfScheduling = (
  options: UseMutationOptions<
    any,
    unknown,
    { personId: string; allowSelfScheduling: boolean }
  >
) => {
  return useMutation<
    any,
    unknown,
    { personId: string; allowSelfScheduling: boolean }
  >({
    mutationFn: ({ personId, allowSelfScheduling }) =>
      patchMemberAllowSelfScheduling(personId, allowSelfScheduling),
    ...options
  });
};

const getMembershipDetail = async (
  personId: string,
  confirmationFormId: string
) => {
  const res = await request<PtMembershipDto>({
    method: 'GET',
    url: `/members/${personId}/training-pass/${confirmationFormId}`
  });
  return res.data;
};

export const useGetMembershipDetail = (
  personId: string,
  confirmationFormId: string,
  options?: UseQueryOptions<PtMembershipDto, AxiosError>
) => {
  return useQuery<PtMembershipDto, AxiosError>(
    queryKeyFactory.GET_MEMBERSHIP_DETAIL(personId, confirmationFormId),
    () => getMembershipDetail(personId, confirmationFormId),
    {
      enabled: !!personId && !!confirmationFormId,
      ...options
    }
  );
};

const getMemberLectureTotalCount = async (personId: string) => {
  const res = await request<FindTotalLectureCountForMemberDto>({
    method: 'GET',
    url: `/members/total-lecture-count/${personId}`
  });
  return res.data;
};

export const useGetMemberLectureTotalCount = (
  personId: string,
  options?: UseQueryOptions<FindTotalLectureCountForMemberDto, AxiosError>
) => {
  return useQuery<FindTotalLectureCountForMemberDto, AxiosError>(
    queryKeyFactory.GET_MEMBER_LECTURE_TOTAL_COUNT(personId),
    () => getMemberLectureTotalCount(personId),
    {
      enabled: !!personId,
      ...options
    }
  );
};
